import React, { useState, useCallback } from "react";
import { View, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { ROLE_LABELS, ROLE_VARIANTS } from "@/constants";
import { Icon } from "@/components/ui/Icon";
import { Feather, FontAwesome6 } from "@expo/vector-icons";
import {
  invitationsApi,
  type InvitationDetail,
  type ActivityLogEntry,
} from "@/lib/api/invitations";
import { ResendInviteModal } from "../components/ResendInviteModal";
import { EmptyState } from "@/components/ui/EmptyState";
import { FlashList } from "@shopify/flash-list";
import { PageFooter } from "@/components/ui/PageFooter";
import { formatRelativeTime, formatAbsoluteDate } from "@/lib/dateFormat";

// ─── Detail Field ─────────────────────────────────────────────────────────────

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

function DetailField({ label, children }: DetailFieldProps) {
  return (
    <View className="gap-1 items-start">
      <AppText weight="medium" className="text-sm text-supporting-text uppercase tracking-wider">
        {label}
      </AppText>
      {children}
    </View>
  );
}

// ─── Activity Log Entry ───────────────────────────────────────────────────────

interface ActivityLogItemProps {
  entry: ActivityLogEntry;
  isLast: boolean;
  sentLabel: string;
  resentLabel: string;
}

function ActivityLogItem({ entry, isLast, sentLabel, resentLabel }: ActivityLogItemProps) {
  const { t, locale } = useI18n();
  const label = (entry.action === "resent" ? resentLabel : sentLabel).replace(
    "{name}",
    entry.actorName
  );
  return (
    <View className="flex-row gap-4 items-start">
      {/* Timeline column */}
      <View className="items-center w-8">
        <View className="w-8 h-8 rounded-full bg-primary-5 items-center justify-center">
          <Icon as={Feather} name="send" size={14} className="text-primary" />
        </View>
        {!isLast && <View className="flex-1 w-px bg-stroke-20 mt-1" />}
      </View>

      {/* Content */}
      <View className={`flex-1 items-start gap-0.5 ${!isLast ? "pb-6" : ""}`}>
        <AppText className="text-sm text-dark-text-50">{label}</AppText>
        <AppText className="text-xs text-dark-text-15">
          {formatRelativeTime(entry.sentAt, locale, t)}
        </AppText>
        <AppText className="text-xs text-dark-text-10">
          {formatAbsoluteDate(entry.sentAt, locale)}
        </AppText>
      </View>
    </View>
  );
}

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function InvitationDetailScreen() {
  const { t, locale } = useI18n();
  const { id } = useLocalSearchParams<{ id: string }>();

  const [resendModalVisible, setResendModalVisible] = useState(false);

  const {
    data: invitation,
    isLoading,
    refetch,
  } = useQuery<InvitationDetail>({
    queryKey: ["invitations", "detail", id],
    queryFn: () => invitationsApi.getById(id!),
    enabled: !!id,
  });

  const handleResent = useCallback(() => {
    setResendModalVisible(false);
    refetch();
  }, [refetch]);

  if (isLoading) {
    return (
      <View className="flex-1 bg-stroke-10 items-center justify-center">
        <AppText className="text-sm text-dark-text-15">{t("common.loading")}</AppText>
      </View>
    );
  }

  return (
    <View className="flex-1 bg-stroke-10">
      {/* ── Breadcrumb ──────────────────────────────────────── */}
      <View className="px-page-x bg-white py-4 border-b border-stroke-50">
        <Breadcrumb
          segments={[
            {
              labelKey: "staff.invitations.detail.breadcrumb",
              href: "/(staff)/(dashboard)/invitations",
            },
            { labelText: invitation?.recipientEmail ?? "" },
          ]}
        />
      </View>
      <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
        {/* ── Page header ─────────────────────────────────────── */}
        <View className="bg-white border-b border-stroke-50 py-6 px-page-x">
          <View className="w-full max-w-8xl self-center flex-row items-start justify-between gap-4">
            <View className="flex-1 me-4 items-start">
              <AppText weight="bold" className="text-2xl text-dark-text-50">
                {invitation?.recipientEmail ?? ""}
              </AppText>
              {invitation?.sentAt && (
                <AppText className="text-sm text-dark-text-15 mt-1">
                  {t("staff.invitations.detail.sentAgo")
                    .replace("{time}", formatRelativeTime(invitation.sentAt, locale, t))
                    .replace("{date}", formatAbsoluteDate(invitation.sentAt, locale))}
                </AppText>
              )}
            </View>
            <Button
              label="staff.invitations.detail.resendInvite"
              onPress={() => setResendModalVisible(true)}
              icon={<Icon as={FontAwesome6} name="rotate" size={16} className="text-white" />}
            />
          </View>
        </View>

        {/* ── Two-column layout ──────────────────────────────── */}
        <View className="px-page-x">
          <View className="flex-col tablet:flex-row gap-6 items-start py-10 w-full max-w-8xl self-center">
            {/* Left column: Invitation details + Activity log */}
            <View className="flex-1 w-full gap-6">
              {/* Invitation Details card */}
              <View className="bg-white border border-stroke-20 rounded-xl overflow-hidden">
                <View className="px-6 py-4 border-b border-stroke-50 mb-6 items-start">
                  <AppText weight="semibold" className="text-base text-dark-text-50">
                    {t("staff.invitations.detail.invitationDetails")}
                  </AppText>
                </View>

                {/* Row 1: Recipient Name + Email */}
                <View className="flex-row gap-6 mb-8 px-6">
                  <View className="flex-1">
                    <DetailField label={t("staff.invitations.detail.recipientName")}>
                      <AppText className="text-sm text-dark-text-50">
                        {invitation?.recipientName ?? "—"}
                      </AppText>
                    </DetailField>
                  </View>
                  <View className="flex-1">
                    <DetailField label={t("staff.invitations.detail.recipientEmail")}>
                      <AppText className="text-sm text-dark-text-50">
                        {invitation?.recipientEmail ?? "—"}
                      </AppText>
                    </DetailField>
                  </View>
                </View>

                {/* Row 2: Last Invitation Date + Last Invited By */}
                <View className="flex-row gap-6 mb-8 px-6">
                  <View className="flex-1">
                    <DetailField label={t("staff.invitations.detail.lastInvitationDate")}>
                      {invitation?.sentAt && (
                        <>
                          <AppText className="text-sm text-dark-text-50">
                            {formatRelativeTime(invitation.sentAt, locale, t)}
                          </AppText>
                          <AppText className="text-xs text-dark-text-15 mt-0.5">
                            {formatAbsoluteDate(invitation.sentAt, locale)}
                          </AppText>
                        </>
                      )}
                    </DetailField>
                  </View>
                  <View className="flex-1">
                    <DetailField label={t("staff.invitations.detail.lastInvitedBy")}>
                      <AppText weight="medium" className="text-sm text-dark-text-50">
                        {invitation?.invitedByName ?? "—"}
                      </AppText>
                      {invitation?.invitedByRole && (
                        <View className="mt-1">
                          <Badge
                            variant={ROLE_VARIANTS[invitation.invitedByRole]}
                            label={ROLE_LABELS[invitation.invitedByRole]}
                          />
                        </View>
                      )}
                    </DetailField>
                  </View>
                </View>

                {/* Row 3: Resend Count */}
                <View className="flex-row gap-6 mb-8 px-6">
                  <DetailField label={t("staff.invitations.detail.resendCount")}>
                    <AppText weight="semibold" className="text-2xl text-dark-text-50">
                      {invitation?.resentCount ?? 0}
                    </AppText>
                  </DetailField>
                </View>
              </View>

              {/* Activity Log card */}
              <View className="bg-white border border-stroke-20 rounded-xl overflow-hidden">
                {/* Header */}
                <View className="px-6 py-4 border-b border-stroke-50 items-start">
                  <AppText weight="semibold" className="text-base text-dark-text-50">
                    {t("staff.invitations.detail.activityLog")}
                  </AppText>
                </View>

                {/* Log entries */}
                <View className="px-6 py-5">
                  {!invitation?.activityLog || invitation.activityLog.length === 0 ? (
                    <EmptyState description="staff.invitations.empty" />
                  ) : (
                    <View className="min-h-[200px] w-full flex-1">
                      <FlashList
                        data={invitation.activityLog}
                        renderItem={({ item, index }) => (
                          <ActivityLogItem
                            entry={item}
                            isLast={index === invitation.activityLog.length - 1}
                            sentLabel={t("staff.invitations.detail.activitySent")}
                            resentLabel={t("staff.invitations.detail.activityResent")}
                          />
                        )}
                      />
                    </View>
                  )}
                </View>
              </View>
            </View>

            {/* Right column: Quick Tip */}
            <View className="w-full tablet:w-72">
              <View className="bg-blue-5 border border-blue-15 rounded-xl p-5 gap-3 items-start">
                <View className="flex-row items-center gap-2">
                  <View className="w-6 h-6 bg-blue-100 rounded-full items-center justify-center">
                    <AppText className="text-xs text-blue-50">💡</AppText>
                  </View>
                  <AppText weight="semibold" className="text-sm text-blue-50">
                    {t("staff.invitations.detail.quickTip")}
                  </AppText>
                </View>
                <AppText className="text-sm text-supporting-text leading-5">
                  {t("staff.invitations.detail.quickTipBody")}
                </AppText>
              </View>
            </View>
          </View>
        </View>
        <PageFooter />
      </ScrollView>

      {/* ── Resend Modal ──────────────────────────────────────── */}
      {invitation && (
        <ResendInviteModal
          visible={resendModalVisible}
          invitationId={invitation.id}
          email={invitation.recipientEmail}
          onDismiss={() => setResendModalVisible(false)}
          onResent={handleResent}
        />
      )}
    </View>
  );
}
